import type { EnterKeyBehavior } from '@web/api/types'
import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'canopy.enter-key-behavior'
export const ENTER_KEY_BEHAVIOR_CHANGED = 'canopy:enter-key-behavior'

export function readLocalEnterKeyBehavior(): EnterKeyBehavior | undefined {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value && ['standard', 'eager', 'pro', 'eager_pro'].includes(value)
      ? (value as EnterKeyBehavior)
      : undefined
  } catch {
    return undefined
  }
}

/** Device preferences work before sign-in and remain usable without a connection. */
export function saveLocalEnterKeyBehavior(value: EnterKeyBehavior): void {
  localStorage.setItem(STORAGE_KEY, value)
  window.dispatchEvent(new Event(ENTER_KEY_BEHAVIOR_CHANGED))
}

function subscribe(listener: () => void) {
  const changed = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener()
  }
  window.addEventListener(ENTER_KEY_BEHAVIOR_CHANGED, listener)
  window.addEventListener('storage', changed)
  return () => {
    window.removeEventListener(ENTER_KEY_BEHAVIOR_CHANGED, listener)
    window.removeEventListener('storage', changed)
  }
}

/** Use the account default until this device has an explicit preference. */
export function useEnterKeyBehavior(
  accountValue?: EnterKeyBehavior,
): EnterKeyBehavior {
  const local = useSyncExternalStore(
    subscribe,
    readLocalEnterKeyBehavior,
    () => undefined,
  )
  return local ?? accountValue ?? 'standard'
}
